<script module lang="ts">
  import Layout from '../components/Layout.svelte'
  import type { SerializedPage } from '../types'

  export const layout = (props: { page: SerializedPage }) => ({
    component: Layout,
    props: { title: props.page.title },
  })
</script>

<script lang="ts">
  import { inertia } from '../utils/inertia'
  import { breadcrumbList } from '../utils/breadcrumbs'
  import { CATEGORIES } from '../categories'
  import SectionHeading from '../components/SectionHeading.svelte'
  import BlockRenderer from '../components/blocks/BlockRenderer.svelte'
  import PageCategoryProvider from '../components/PageCategoryProvider.svelte'
  import StructuredData from '../components/StructuredData.svelte'
  import TableOfContents from '../components/TableOfContents.svelte'
  import { getI18n } from '../i18n'
  import type { SerializedBlock, SerializedHeaderBlock } from '../types'

  const MEDIA_BLOCK_TYPES: SerializedBlock['type'][] = ['ImageBlock', 'ImageGalleryBlock', 'VideoBlock', 'VideoGalleryBlock']
  // Media among the first blocks is likely the largest element in view when the page loads.
  const PRIORITY_BLOCK_RANGE = 3

  let { page, structuredData }: { page: SerializedPage; structuredData: Record<string, unknown> & { url: string } } = $props()

  const i18n = getI18n()
  const backHref = $derived(i18n.path(`/pages/${page.category}`))
  const breadcrumbs = $derived(
    breadcrumbList(
      [
        { name: i18n.t('navigation.home'), path: i18n.path('/welcome') },
        { name: i18n.t(`categories.${page.category}.name`), path: backHref },
        { name: page.title, path: i18n.path(`/pages/${page.category}/${page.id}`) },
      ],
      structuredData.url,
    ),
  )
  const tableOfContentsEntries = $derived(
    page.blocks
      .filter((block): block is SerializedHeaderBlock => block.type === 'HeaderBlock')
      .map((block) => ({ id: `block-${block.id}`, title: block.title, level: block.level })),
  )
  const priorityBlockId = $derived(
    page.blocks.slice(0, PRIORITY_BLOCK_RANGE).find((block) => block.type !== 'PreviewImageBlock' && MEDIA_BLOCK_TYPES.includes(block.type))?.id,
  )
</script>

<svelte:head>
  {#if page.description}
    <meta name="description" content={page.description} />
  {/if}
</svelte:head>

<StructuredData data={structuredData} />
<StructuredData data={breadcrumbs} />

<PageCategoryProvider category={page.category}>
  <div class="page-layout">
    <aside class="page-table-of-contents">
      <TableOfContents entries={tableOfContentsEntries} />
    </aside>

    <div class="page-content">
      <div class="back-action">
        <a href={backHref} {@attach inertia({ href: backHref })} class="back-action-link">{i18n.t('page.back')}</a>
      </div>
      <div class="page-heading">
        <SectionHeading eyebrow={i18n.t(`categories.${page.category}.name`)} variant={CATEGORIES[page.category].variant}>{page.title}</SectionHeading>
      </div>
      {#each page.blocks as block (block.id)}
        <BlockRenderer {block} priority={block.id === priorityBlockId} />
      {/each}
    </div>
  </div>
</PageCategoryProvider>

<style>
  .page-layout {
    display: flex;
    flex-direction: column;
    gap: var(--space-9);
  }

  @media (--desktop) {
    .page-layout {
      display: grid;
      grid-template-columns: 190px minmax(0, 1fr);
      column-gap: var(--space-10);
    }
  }

  /* Breakout grid: blocks sit in the content column, media may span the full column into the page gutter.
     The negative margin cancels the layout's gutter so the grid can own it as its outer tracks. */
  .page-content {
    display: grid;
    grid-template-columns:
      [full-start] var(--page-gutter)
      [content-start] minmax(0, var(--content-max-width))
      [content-end] var(--page-gutter)
      [full-end];
    justify-content: center;
    align-content: start;
    margin-inline: calc(var(--page-gutter) * -1);
    padding: var(--space-9) 0;
    min-width: 0;
  }

  .back-action {
    grid-column: content;
    margin-bottom: var(--space-7);
  }

  .page-heading {
    grid-column: content;
    margin-bottom: var(--space-9);
  }

  .back-action-link {
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 2px;
    transition: color var(--transition-medium);
  }

  .back-action-link:hover {
    color: var(--color-text-muted);
  }
</style>
